// App-scoped resources — extracted from api.js (Tier 2 #9a round 3).
// Knowledge entries, message/excel/etc. templates, datasets, react
// components, and per-app git config. All are app-scoped helpers that
// don't fit into the broader views/screens/integrations buckets.

import api from './api';

// ─── Knowledge Entries ─────────────────────────────────────────────────────
export const fetchKnowledgeEntries = async (appId, params = {}) => {
  const query = new URLSearchParams(params).toString();
  const url = `/apps/${appId}/knowledge${query ? `?${query}` : ''}`;
  const response = await api.get(url);
  return response.data;
};

export const createKnowledgeEntry = async (appId, data) => {
  const response = await api.post(`/apps/${appId}/knowledge`, data);
  return response.data;
};

export const updateKnowledgeEntry = async (appId, entryId, data) => {
  const response = await api.put(`/apps/${appId}/knowledge/${entryId}`, data);
  return response.data;
};

export const deleteKnowledgeEntry = async (appId, entryId) => {
  const response = await api.delete(`/apps/${appId}/knowledge/${entryId}`);
  return response.data;
};

// ─── App Templates (email / excel / doc / slack / sms / whatsapp / http) ───
export const fetchAppTemplates = async (appId, type, { signal } = {}) => {
    const params = type ? `?type=${type}` : '';
    const response = await api.get(`/apps/${appId}/templates${params}`, { signal });
    return response.data;
};

export const fetchAppTemplate = async (appId, templateUuid) => {
    const response = await api.get(`/apps/${appId}/templates/${templateUuid}`);
    return response.data;
};

export const createAppTemplate = async (appId, data) => {
    const response = await api.post(`/apps/${appId}/templates`, data);
    return response.data;
};

export const updateAppTemplate = async (appId, templateUuid, data) => {
    const response = await api.put(`/apps/${appId}/templates/${templateUuid}`, data);
    return response.data;
};

export const deleteAppTemplate = async (appId, templateUuid) => {
    const response = await api.delete(`/apps/${appId}/templates/${templateUuid}`);
    return response.data;
};

// ─── Teams Bot Channels ────────────────────────────────────────────────────
// Returns [{team_id, team_name, channel_id, channel_name}] for all channels
// inside teams where ZinoBot is installed (resolved via Graph API server-side).
export const fetchTeamsChannels = async (appId, { signal } = {}) => {
    const response = await api.get(`/apps/${appId}/integrations/teams/channels`, { signal });
    return response.data;
};

export const fetchPingbixWhatsAppTemplates = async (appId) => {
    const response = await api.get(`/apps/${appId}/whatsapp/pingbix-templates`);
    return response.data;
};

export const fetchPingbixWhatsAppTemplatesByOrg = async (orgId) => {
    const response = await api.get(`/orgs/${orgId}/whatsapp/pingbix-templates`);
    return response.data;
};

export const uploadTemplateFile = async (appId, templateUuid, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/apps/${appId}/templates/${templateUuid}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
};

// ─── Datasets ──────────────────────────────────────────────────────────────
export const fetchDatasets = async (appId) => {
  const response = await api.get(`/apps/${appId}/datasets`);
  return response.data;
};

export const createDataset = async (appId, data) => {
  const response = await api.post(`/apps/${appId}/datasets`, data);
  return response.data;
};

// Phase Z.2 uuid-keyed dataset helpers (JSONB carries a uuid, not a PK).
export const fetchDatasetByUUID = async (appId, datasetUuid) => {
  const response = await api.get(`/apps/${appId}/datasets/${datasetUuid}`);
  return response.data;
};
export const updateDatasetByUUID = async (appId, datasetUuid, data) => {
  const response = await api.put(`/apps/${appId}/datasets/${datasetUuid}`, data);
  return response.data;
};
export const deleteDatasetByUUID = async (appId, datasetUuid) => {
  const response = await api.delete(`/apps/${appId}/datasets/${datasetUuid}`);
  return response.data;
};

// ─── React Components ──────────────────────────────────────────────────────
export const fetchReactComponentsByApp = async (appId, { signal } = {}) => {
  const response = await api.get(`/apps/${appId}/react-components`, { signal });
  return response.data;
};

export const createReactComponent = async (appId, data) => {
  const response = await api.post(`/apps/${appId}/react-components`, data);
  return response.data;
};

// Phase Z.2 uuid-keyed react-component helpers.
export const fetchReactComponentByUUID = async (appId, rcUuid) => {
  const response = await api.get(`/apps/${appId}/react-components/${rcUuid}`);
  return response.data;
};
export const updateReactComponentByUUID = async (appId, rcUuid, data) => {
  const response = await api.put(`/apps/${appId}/react-components/${rcUuid}`, data);
  return response.data;
};
export const deleteReactComponentByUUID = async (appId, rcUuid) => {
  const response = await api.delete(`/apps/${appId}/react-components/${rcUuid}`);
  return response.data;
};

// Builder-only: test a DRAFT http_binding before it is saved/deployed.
// payload: { template_uuid, variables, values } — see rc_binding_test_handler.go.
// NOT the runtime call path (that's view-service's
// /app/{appId}/view/react-components/call via the frozen executor).
export const testReactComponentBinding = async (appId, payload) => {
  const response = await api.post(`/apps/${appId}/react-components/test-binding`, payload);
  return response.data;
};

// ─── App Git Config ────────────────────────────────────────────────────────
export const fetchGitConfig = async (appId) => {
    const response = await api.get(`/apps/${appId}/git-config`);
    return response.data;
};

export const updateGitConfig = async (appId, config) => {
    const response = await api.put(`/apps/${appId}/git-config`, config);
    return response.data;
};

export const deleteGitConfig = async (appId) => {
    const response = await api.delete(`/apps/${appId}/git-config`);
    return response.data;
};

export const testGitConnection = async (appId) => {
    const response = await api.post(`/apps/${appId}/git-config/test`);
    return response.data;
};
